import { Box, Card, CardContent, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import type { CollectionPace } from '../../lib/dashboard-health';
import { chartColors } from '../../theme';
import { ChartLegend } from '../ChartLegend';
import { useT } from '../../i18n';

function monthName(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return new Date(y, m - 1, 1).toLocaleDateString('id-ID', { month: 'short' });
}

/**
 * How much of this month's bills are paid so far, day by day, beside last
 * month's curve, with the takeaway in one sentence.
 */
export function CollectionPaceCard({ pace }: { pace: CollectionPace }) {
  const t = useT();
  const theme = useTheme();
  const blue = chartColors(theme.palette.mode).primary;
  const grey = alpha(theme.palette.text.primary, 0.35);
  const days = pace.previous.byDay.length;
  const xs = Array.from({ length: Math.max(days, pace.day) }, (_, i) => i + 1);
  const pct = (r: number) => Math.round(r * 100);
  const diff = pct(pace.rateToday) - pct(pace.ratePreviousSameDay);
  const current = monthName(pace.current.month);
  const previous = monthName(pace.previous.month);

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600}>{t('dash.pace.title')}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {pace.current.billedCents === 0
            ? t('dash.pace.noBills')
            : t(diff >= 0 ? 'dash.pace.ahead' : 'dash.pace.behind', {
                day: pace.day,
                now: pct(pace.rateToday),
                then: pct(pace.ratePreviousSameDay),
                month: previous,
              })}
        </Typography>
        <Box sx={{ mx: -1 }}>
          <LineChart
            height={220}
            margin={{ top: 16, right: 16, bottom: 28, left: 40 }}
            xAxis={[{ data: xs, scaleType: 'point', tickInterval: (d: number) => d === 1 || d % 5 === 0, valueFormatter: (d: number, ctx) => (ctx.location === 'tooltip' ? t('dash.pace.dayN', { day: d }) : String(d)) }]}
            yAxis={[{ min: 0, max: 100, valueFormatter: (v: number) => `${v}%` }]}
            series={[
              {
                id: 'previous',
                label: previous,
                data: xs.map((d) => (d <= days ? pct(pace.previous.byDay[d - 1]!) : null)),
                color: grey,
                showMark: false,
                curve: 'monotoneX',
                valueFormatter: (v) => (v === null ? '—' : `${v}%`),
              },
              {
                id: 'current',
                label: current,
                data: xs.map((d) => (d <= pace.day ? pct(pace.current.byDay[d - 1]!) : null)),
                color: blue,
                area: true,
                showMark: false,
                curve: 'monotoneX',
                valueFormatter: (v) => (v === null ? '—' : `${v}%`),
              },
            ]}
            slotProps={{ legend: { hidden: true } }}
            grid={{ horizontal: true }}
            sx={{
              '& .MuiLineChart-line[data-series="previous"], & .MuiLineElement-series-previous': { strokeDasharray: '5 4' },
              '& .MuiAreaElement-series-current': { fill: alpha(blue, 0.12) },
            }}
          />
        </Box>
        <ChartLegend
          items={[
            { label: t('dash.pace.thisMonth', { month: current }), color: blue, kind: 'line' },
            { label: t('dash.pace.lastMonth', { month: previous }), color: grey, kind: 'line' },
          ]}
        />
      </CardContent>
    </Card>
  );
}
